import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { type FormEvent } from 'react'

export interface RoleOption {
  id: number
  name: string
  description: string
}

export interface AddMemberFormState {
  name: string
  email: string
  roleId: number
}

export interface AddMemberDialogProps {
  isOpen: boolean
  onClose: () => void
  formState: AddMemberFormState
  onNameChange: (value: string) => void
  onEmailChange: (value: string) => void
  onRoleChange: (value: number) => void
  onSubmit: () => void
  isSubmitting: boolean
  roles: RoleOption[]
  error?: string | null
}

const AddMemberDialog = ({
  isOpen,
  onClose,
  formState,
  onNameChange,
  onEmailChange,
  onRoleChange,
  onSubmit,
  isSubmitting,
  roles,
  error,
}: AddMemberDialogProps) => {
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    onSubmit()
  }

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-5 py-6 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95">
            <div className="flex items-start justify-between">
              <DialogTitle
                as="h3"
                className="text-lg font-semibold text-gray-900">
                Invite a club member
              </DialogTitle>
              <button
                type="button"
                onClick={onClose}
                className="-m-2 rounded-md p-2 text-gray-400 hover:text-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-5" />
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Share the details below to email a club invite with the right
              level of access.
            </p>

            <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="add-member-email"
                  className="block text-sm font-medium text-gray-900">
                  Email
                </label>
                <input
                  id="add-member-email"
                  name="email"
                  type="email"
                  value={formState.email}
                  onChange={(event) => onEmailChange(event.target.value)}
                  placeholder="name@example.com"
                  className="mt-1 block w-full rounded-md border-0 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600"
                  autoComplete="email"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="add-member-name"
                  className="block text-sm font-medium text-gray-900">
                  Full name
                </label>
                <input
                  id="add-member-name"
                  name="name"
                  type="text"
                  value={formState.name}
                  onChange={(event) => onNameChange(event.target.value)}
                  placeholder="Enter full name"
                  className="mt-1 block w-full rounded-md border-0 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600"
                  autoComplete="name"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="add-member-role"
                  className="block text-sm font-medium text-gray-900">
                  Club access role
                </label>
                <div className="mt-1">
                  <select
                    id="add-member-role"
                    name="role"
                    value={formState.roleId}
                    onChange={(event) =>
                      onRoleChange(Number(event.target.value))
                    }
                    className="block w-full rounded-md border-0 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600">
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name} - {role.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-600" role="alert">
                  {error}
                </p>
              )}

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:w-auto">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
                  {isSubmitting ? 'Sending...' : 'Send invite'}
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}

export default AddMemberDialog
